import {
  BatchLoadExperimentsData,
  EValidationResult,
  ValidationStatus,
} from 'business/models/Models';
import { BatchLoadExperimentService } from 'business/services/BatchLoadExperimentService';
import { RMModal } from 'components/modal';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { formatBytes, readFileAsBase64String } from 'utils/Global';
import BatchLoadExperimentTable from './BatchLoadExperimentTable';
import BatchLoadExperimentWizardContext from './BatchLoadExperimentWizardContext';
import { FmlxButton, FmlxProgressBar } from 'fmlx-common-ui';
import { FolderHierarchyEditor } from './FolderHierarchyEditor';

enum ETableFilterSelection {
  All = 0,
  Valid = 1,
  Warning = 2,
  Invalid = 3,
}

export default function ImportCSVFile() {
  const inputFileRef = useRef(null);
  const downloadTemplateRef = useRef(null);
  const batchLoadWizardContext = useContext(BatchLoadExperimentWizardContext);
  const hasExistingData = batchLoadWizardContext.batchLoadExperimentsDTO != null;
  const [loading, setLoading] = useState<{ show: boolean; text: string }>({
    show: hasExistingData,
    text: hasExistingData ? 'Loading file preview...' : 'Loading',
  });
  const [showData, setShowData] = useState(false);
  const [showBrowseFileText, setShowBrowseFileText] = useState(!hasExistingData);
  const [showFileNotLoadedError, setShowFileNotLoadedError] = useState(false);

  const [batchLoadExperimentData, setBatchLoadExperimentData] = useState<
    BatchLoadExperimentsData[]
  >(() => []);

  const [tableData, setTableData] = useState<BatchLoadExperimentsData[]>([]);
  const [experimentCount, setExperimentCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [sampleCount, setSampleCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState<ETableFilterSelection>(
    ETableFilterSelection.All,
  );
  const batchLoadExperimentService = new BatchLoadExperimentService();
  const data = batchLoadWizardContext.batchLoadExperimentsDTO;

  const statusOptions = Object.values(ETableFilterSelection)
    .filter((v): v is ETableFilterSelection => typeof v === 'number')
    .map((value) => ({
      id: value,
      name: ETableFilterSelection[value],
    }));

  const isStatusValid = (status: ValidationStatus) => {
    return status.result !== EValidationResult.Invalid;
  };

  useEffect(() => {
    if (data == null || data == undefined) {
      setShowBrowseFileText(true);
      setShowData(false);
      batchLoadWizardContext.setNextButtonDisallowed(true);
      return;
    }

    if (data.status && isStatusValid(data.status)) {
      setShowBrowseFileText(false);
      setLoading({ show: true, text: 'Loading file preview...' });
      setTableData(
        data.batchLoadExperimentsData.sort(
          (a, b) => a.status.validation.result - b.status.validation.result,
        ),
      );
      setBatchLoadExperimentData(data.batchLoadExperimentsData);
      setExperimentCount(data.experimentCount);
      setProjectCount(data.projectCount);
      setSampleCount(data.sampleCount);
      setWarningCount(data.warningCount);
      setErrorCount(data.errorCount);
      if (data.errorCount == 0) {
        batchLoadWizardContext.setExperimentData(data.batchLoadExperimentsData);
        batchLoadWizardContext.setNextButtonDisallowed(false);
      } else {
        batchLoadWizardContext.setNextButtonDisallowed(true);
      }
      setShowData(true);
      setLoading({ show: false, text: 'File Loaded' });
    }
  }, [data]);

  useEffect(() => {
    if (batchLoadExperimentData.length == 0) {
      return;
    }

    switch (selectedStatus) {
      case ETableFilterSelection.All:
        setTableData(batchLoadExperimentData);
        if (data.errorCount == 0) {
          batchLoadWizardContext.setNextButtonDisallowed(false);
        } else {
          batchLoadWizardContext.setNextButtonDisallowed(true);
        }
        break;

      case ETableFilterSelection.Valid:
        const validData = batchLoadExperimentData.filter(
          (row) => row.status.validation.result === EValidationResult.Valid,
        );
        setTableData(validData);
        if (validData.length === batchLoadExperimentData.length) {
          batchLoadWizardContext.setNextButtonDisallowed(false);
        }
        break;

      case ETableFilterSelection.Warning:
        const warningData = batchLoadExperimentData.filter(
          (row) => row.status.validation.result === EValidationResult.Warning,
        );
        setTableData(warningData);
        if (warningData.length === batchLoadExperimentData.length) {
          batchLoadWizardContext.setNextButtonDisallowed(false);
        }
        break;

      case ETableFilterSelection.Invalid:
        const errorData = batchLoadExperimentData.filter(
          (row) => row.status?.validation?.result === EValidationResult.Invalid,
        );
        setTableData(errorData);
        batchLoadWizardContext.setNextButtonDisallowed(true);
        break;

      default:
        break;
    }
  }, [selectedStatus]);

  function onTableReady() {
    if (loading.show) {
      setLoading({ show: false, text: 'File Loaded' });
    }
  }

  function addAttachmentHandler() {
    if (inputFileRef) {
      inputFileRef.current.click();
    }
  }

  function downloadTemplateHandler() {
    if (downloadTemplateRef) {
      downloadTemplateRef.current.click();
    }
  }

  async function attachmentChangedHandler(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files[0];
    if (!file.type.includes('csv') && !file.type.includes('application/vnd.ms-excel')) {
      batchLoadWizardContext.setBatchLoadExperimentsDTO(null);
      setShowFileNotLoadedError(true);
      setShowBrowseFileText(true);
      return;
    }

    await readFileAsBase64String(file, validateCsvData);
    batchLoadWizardContext.setFileName(file.name);
    const fileSize = formatBytes(file.size);
    batchLoadWizardContext.setFileSize(fileSize);
    e.target.value = '';
  }

  async function validateCsvData(base64String: string) {
    setLoading({ show: true, text: 'Loading file preview...' });
    const response = await batchLoadExperimentService.validateInputCsvFile(base64String);
    if (!isStatusValid(response.status)) {
      batchLoadWizardContext.setBatchLoadExperimentsDTO(null);
      setShowFileNotLoadedError(true);
      setShowBrowseFileText(true);
      setLoading({ show: false, text: '' });
      return;
    } else {
      batchLoadWizardContext.setBatchLoadExperimentsDTO(response);
    }
  }

  function onFilterChange(e) {
    const value = Number(e.target.value) as ETableFilterSelection;
    setSelectedStatus(value);
    batchLoadWizardContext.setSelectedStatus(value);
  }

  return (
    <div id="importcsvfile-wrapper" className="flex flex-col h-[60vh]">
      <div id="importcsvfile-button-container" className="flex flex-col">
        <div
          id="importcsvfile-button-wrapper"
          className="flex flew-row items-center gap-2"
        >
          <div className="font-bold text-[#888888]">Select CSV File</div>
          <FmlxButton
            id="importcsvfile-fmlxbutton-browsefile"
            label="BROWSE FILE"
            type={FmlxButton.Type.POSITIVE}
            onClick={addAttachmentHandler}
          />
          <input
            id="importcsvfile-input-browsefile"
            aria-label="a"
            className="mr-[10px] hidden"
            type="file"
            accept={'.csv'}
            ref={inputFileRef}
            onInput={attachmentChangedHandler}
          />
          <FmlxButton
            id="importcsvfile-fmlxbutton-downloadexample"
            label="DOWNLOAD TEMPLATE"
            type={FmlxButton.Type.POSITIVE}
            variant={FmlxButton.Variant.OUTLINE}
            onClick={downloadTemplateHandler}
          />
          <a
            id="importcsvfile-anchor-downloadexample"
            ref={downloadTemplateRef}
            className="hidden"
            title="download-batchload-template"
            href="/assets/import files/BatchLoadTemplate.csv"
            download="BatchLoadTemplate.csv"
          >
            BatchLoad Template
          </a>
        </div>
      </div>
      {showBrowseFileText && loading.show === false && (
        <div
          id="importcsvfile-div-pleaseselectfiletext"
          className="flex grow align-center justify-center text-center text-[gainsboro]"
        >
          {`Please select "Browse File" to import .csv file`}
        </div>
      )}
      <RMModal
        id="importcsvfile-rmmodal-errormodal"
        showModal={showFileNotLoadedError}
        setShowModal={setShowFileNotLoadedError}
        body="Either file format or file data is invalid. Please make sure you have added all mandatory columns and the rows are in correct format i.e. no missing value or at least one non empty data row."
        heading="Error"
        onOk={() => {
          setShowFileNotLoadedError(false);
        }}
        okButtonText="OK"
        isOkOnly
      />
      {loading.show && (
        <div
          id="importcsvfile-loading-wrapper"
          className="flex flex-col h-full w-full justify-center items-center gap-4"
        >
          <FmlxProgressBar
            id="importcsvfile-loading-progress-bar"
            variant={FmlxProgressBar.Variant.INDETERMINATE}
            size={FmlxProgressBar.Size.MEDIUM}
          />
          <div id="importcsvfile-loading-text" className="font-bold">
            {loading.text}
          </div>
          <div
            id="importcsvfile-loading-batchloadwizardcontext-wrapper"
            className="flex flex-row w-1/4 justify-between items-center gap-2"
          >
            <div
              id="importcsvfile-loading-batchloadwizardcontext-filename"
              className="w-3/4 text-sm"
            >
              {batchLoadWizardContext.fileName}
            </div>
            <div
              id="importcsvfile-loading-batchloadwizardcontext-filesize"
              className="w-1/4 text-right text-sm text-gray-400 no-wrap"
            >
              {batchLoadWizardContext.fileSize}
            </div>
          </div>
        </div>
      )}
      {showData && loading.show === false && (
        <div
          id="importcsvfile-showdata-wrapper"
          className="flex flex-col gap-2 my-2 h-full min-h-0"
        >
          <FolderHierarchyEditor />
          <div
            id="importcsvfile-showdata-header"
            className="flex flex-row justify-between items-end"
          >
            <div
              id="importcsvfile-showdata-preview-wrapper"
              className="flex flex-row gap-1"
            >
              <span
                id="importcsvfile-showdata-preview-title"
                className="font-bold text-sm"
              >
                Preview :
              </span>
              <span
                id="importcsvfile-span-previewfilename"
                className="font-medium text-[#888888] text-sm"
              >
                ({batchLoadWizardContext.fileName})
              </span>
            </div>
            <div
              id="importcsvfile-showdata-filterbystatus-wrapper"
              className="flex flex-row items-center align-top gap-2"
            >
              <span id="importcsvfile-showdata-filterbystatus-title">
                Filter by Status
              </span>
              <span
                id="importcsvfile-showdata-filterbystatus-select"
                className="inline-block w-[100px]"
              >
                <RMSelect
                  id="importcsvfile-rmselect-status"
                  className="flex-50 h-8"
                  options={statusOptions}
                  selectedValue={selectedStatus}
                  onChange={(e) => onFilterChange(e)}
                  placeholder={'any'}
                />
              </span>
            </div>
          </div>
          <div
            id="importcsvfile-showdata-batchloadexperimenttable"
            className="flex h-[35vh] overflow-auto"
          >
            <BatchLoadExperimentTable tableData={tableData} onReady={onTableReady} />
          </div>
          <div
            id="importcsvfile-showdata-details-wrapper"
            className="flex flex-row w-3/4 gap-2"
          >
            <div id="importcsvfile-div-expcount" className="flex grow">
              Exp. Count: {experimentCount}
            </div>
            <div id="importcsvfile-div-projectcount" className="flex grow">
              Project Count: {projectCount}
            </div>
            <div id="importcsvfile-div-samplecount" className="flex grow">
              Sample Count: {sampleCount}
            </div>
            <div id="importcsvfile-div-warningcount" className="flex grow">
              Warning Count: {warningCount}
            </div>
            <div id="importcsvfile-div-errorcount" className="flex grow">
              Error Count: {errorCount}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
